'use client'

import { useRef, useState, type ChangeEvent } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import { Check, Copy, Download, FileText, KeyRound, Trash2, Upload } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  MAX_DOCUMENT_BYTES,
  uploadCaseDocumentSchema,
  type UploadCaseDocumentFormValues,
} from '@/features/cases/schemas/case-forms.schema'
import { formatDateTime } from '@/lib/format/date'
import { normalizeDownloadUrl } from '@/features/cases/lib/normalize-download-url'
import { DOCUMENT_KIND_LABEL, DOCUMENT_STATUS_LABEL } from '@/features/cases/types'
import type {
  CaseDocument,
  CaseDocumentDownload,
  CaseDocumentKind,
  CaseDocumentPasswordResult,
  DocumentStatus,
} from '@/features/cases/types'

/**
 * File-type badge keyed off the extension rather than the document's `kind`, because the kind is
 * already spelled out in the row's description ("Portfolio Statement · 60 KB · …") while the format
 * was previously nowhere on screen.
 *
 * Solid fill with white text, on the 600/700 step of a primitive ramp rather than a tint of the
 * color, since a 9px label needs 4.5:1 contrast that a tint of its own hue can't reliably reach, and
 * the primitives (unlike the semantic --text-* tokens) stay theme-stable.
 */
const FILE_TYPE_FILL: Record<string, string> = {
  pdf: 'var(--color-red-600)',
  csv: 'var(--color-green-700)',
  xls: 'var(--color-green-700)',
  xlsx: 'var(--color-green-700)',
  doc: 'var(--color-blue-600)',
  docx: 'var(--color-blue-600)',
  png: 'var(--color-yellow-700)',
  jpg: 'var(--color-yellow-700)',
  jpeg: 'var(--color-yellow-700)',
}

const FILE_TYPE_FALLBACK_FILL = 'var(--color-slate-700)'

function extensionOf(fileName: string) {
  // Last dot only, and never treat a leading-dot name (".gitignore") as all-extension.
  const dot = fileName.lastIndexOf('.')
  if (dot <= 0 || dot === fileName.length - 1) return null
  return fileName.slice(dot + 1).toLowerCase()
}

function FileTypeBadge({ fileName }: { fileName: string }) {
  const ext = extensionOf(fileName)
  const fill = (ext ? FILE_TYPE_FILL[ext] : undefined) ?? FILE_TYPE_FALLBACK_FILL
  return (
    <span
      className="flex size-9 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg text-white"
      style={{ backgroundColor: fill }}
      // The extension is decorative here — the filename beside it already carries it for a reader
      // and a screen reader, so announcing "PDF" again would just be noise.
      aria-hidden="true"
    >
      {ext ? (
        // Long extensions would blow out a 36px tile, so anything over 4 chars falls back to a glyph.
        ext.length <= 4 ? (
          <span className="text-[9px] font-bold uppercase leading-none">{ext}</span>
        ) : (
          <FileText className="size-4" />
        )
      ) : (
        <FileText className="size-4" />
      )}
    </span>
  )
}

const STATUS_VARIANT: Record<DocumentStatus, 'secondary' | 'outline' | 'destructive'> = {
  received: 'outline',
  parsed: 'secondary',
  failed: 'destructive',
}

function formatSize(kb: number) {
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`
}

const KIND_OPTIONS: { value: CaseDocumentKind; label: string }[] = [
  { value: 'cas_pdf', label: 'CAS Statement' },
  { value: 'broker_xlsx', label: 'Tradebook' },
  { value: 'advisor_report', label: 'Advisor Report' },
]

/** New in Phase 7 — a real multipart upload with progress, replacing the old bare
 * `<input type="file">`. Only rendered on the real (staff) path; the mock path keeps its original
 * "Attach report" button/behavior unchanged. */
function UploadDocumentDialog({
  open,
  onOpenChange,
  onUpload,
  uploadProgress,
  isUploading,
  onCancelUpload,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpload: (input: { file: File; kind: CaseDocumentKind; password?: string }) => Promise<void>
  uploadProgress: number
  isUploading: boolean
  onCancelUpload: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [sizeError, setSizeError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const form = useForm<
    z.input<typeof uploadCaseDocumentSchema>,
    unknown,
    UploadCaseDocumentFormValues
  >({
    resolver: zodResolver(uploadCaseDocumentSchema),
    mode: 'onTouched',
    defaultValues: { kind: 'cas_pdf', password: '' },
  })
  const kind = form.watch('kind')

  const reset = () => {
    setFile(null)
    setSizeError(null)
    form.reset({ kind: 'cas_pdf', password: '' })
  }

  const pickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    e.target.value = ''
    if (!picked) return
    if (picked.size > MAX_DOCUMENT_BYTES) {
      setSizeError('That file is over 20 MB — split it or compress it before uploading.')
      setFile(null)
      return
    }
    setSizeError(null)
    setFile(picked)
  }

  const onSubmit = form.handleSubmit(async (values) => {
    if (!file) return
    await onUpload({
      file,
      kind: values.kind,
      password: values.kind === 'cas_pdf' ? values.password || undefined : undefined,
    })
    reset()
    onOpenChange(false)
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next && !isUploading) reset()
      }}
    >
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Upload document</DialogTitle>
            <DialogDescription>Attach a CAS statement, tradebook, or report.</DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="doc-kind">Kind</FieldLabel>
              <Select
                value={kind}
                onValueChange={(v) =>
                  form.setValue('kind', v as UploadCaseDocumentFormValues['kind'])
                }
              >
                <SelectTrigger id="doc-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {kind === 'cas_pdf' && (
              <Field>
                <FieldLabel htmlFor="doc-password">CAS password (optional)</FieldLabel>
                <Input id="doc-password" {...form.register('password')} />
                <FieldError errors={[form.formState.errors.password]} />
              </Field>
            )}
            <Field>
              <FieldLabel>File</FieldLabel>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => inputRef.current?.click()}
                  disabled={isUploading}
                >
                  Choose file
                </Button>
                <span className="text-muted-foreground truncate text-sm">
                  {file ? file.name : 'No file selected'}
                </span>
                <input ref={inputRef} type="file" className="hidden" onChange={pickFile} />
              </div>
              {sizeError && <p className="text-destructive text-sm">{sizeError}</p>}
            </Field>
            {isUploading && (
              <div className="flex flex-col gap-1.5">
                <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
                  <div
                    className="bg-primary h-full rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-muted-foreground text-xs">{uploadProgress}%</p>
              </div>
            )}
          </FieldGroup>
          <DialogFooter>
            {isUploading ? (
              <Button type="button" variant="outline" onClick={onCancelUpload}>
                Cancel upload
              </Button>
            ) : (
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
            )}
            <Button type="submit" disabled={!file || isUploading}>
              {isUploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Modeled on `admin-staff-view.tsx`'s `PasswordResultDialog` — a read-only field plus a
 * copy-to-clipboard icon and a "copied" flash state. Owned entirely by `CaseDocuments`, so
 * `client-detail.tsx` only ever supplies the plain async fetch. */
function PasswordRevealDialog({
  result,
  onOpenChange,
}: {
  result: CaseDocumentPasswordResult | null
  onOpenChange: (open: boolean) => void
}) {
  const [copied, setCopied] = useState(false)

  const copyPassword = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result.password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog
      open={!!result}
      onOpenChange={(open) => {
        if (!open) setCopied(false)
        onOpenChange(open)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>CAS statement password</DialogTitle>
          <DialogDescription>
            The depository-issued password on this statement — not an account credential.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 py-2">
          <Input readOnly value={result?.password ?? ''} className="font-mono" />
          <Button type="button" variant="outline" size="icon" onClick={copyPassword}>
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          </Button>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button">Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The documents attached to a case — the CAS/tradebook the investor uploaded and the report the
 * advisor sends back. `client-detail.tsx` (staff/admin) is this component's only caller — the
 * investor dashboard has no access to `GET /cases/:id/documents` at all (staff/admin-only RBAC),
 * so no investor screen renders it.
 */
export function CaseDocuments({
  canAttachReport = false,
  injectedDocuments,
  onUpload,
  uploadProgress = 0,
  isUploading = false,
  onCancelUpload,
  onDelete,
  onDownload,
  onGetPassword,
}: {
  canAttachReport?: boolean
  /** Real data, fed in by `client-detail.tsx`. */
  injectedDocuments?: CaseDocument[]
  /** Injected real mutations/actions — when provided, this component's upload/delete/download/
   * password actions call the real backend instead of the mock store. The deferred investor
   * dashboard omits all of these and keeps its original mock-only behavior unchanged. */
  onUpload?: (input: { file: File; kind: CaseDocumentKind; password?: string }) => Promise<void>
  uploadProgress?: number
  isUploading?: boolean
  onCancelUpload?: () => void
  onDelete?: (documentId: string) => void
  onDownload?: (documentId: string) => Promise<CaseDocumentDownload>
  onGetPassword?: (documentId: string) => Promise<CaseDocumentPasswordResult>
}) {
  const documents = injectedDocuments ?? []
  const [uploadOpen, setUploadOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<CaseDocument | null>(null)
  const [passwordResult, setPasswordResult] = useState<CaseDocumentPasswordResult | null>(null)

  const caseDocs = [...documents].sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1))

  const handleDownload = async (doc: CaseDocument) => {
    if (!onDownload) return
    const { url } = await onDownload(doc.id)
    window.open(normalizeDownloadUrl(url), '_blank', 'noopener,noreferrer')
  }

  const handleGetPassword = async (doc: CaseDocument) => {
    if (!onGetPassword) return
    const result = await onGetPassword(doc.id)
    setPasswordResult(result)
  }

  const confirmDelete = () => {
    if (deleteTarget && onDelete) onDelete(deleteTarget.id)
    setDeleteTarget(null)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Documents</p>
        {canAttachReport && onUpload && (
          <Button type="button" size="sm" variant="outline" onClick={() => setUploadOpen(true)}>
            <Upload className="size-3.5" /> Upload document
          </Button>
        )}
      </div>

      {caseDocs.length === 0 ? (
        <Empty className="border border-dashed py-8">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileText />
            </EmptyMedia>
            <EmptyTitle>No documents yet</EmptyTitle>
            <EmptyDescription>Nothing has been uploaded for this case.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ItemGroup className="gap-2">
          {caseDocs.map((doc) => {
            return (
              <Item key={doc.id} variant="outline" size="sm">
                <ItemMedia>
                  <FileTypeBadge fileName={doc.fileName} />
                </ItemMedia>
                <ItemContent>
                  <ItemTitle className="truncate">{doc.fileName}</ItemTitle>
                  <ItemDescription>
                    {DOCUMENT_KIND_LABEL[doc.kind]} · {formatSize(doc.sizeKb)} ·{' '}
                    {formatDateTime(doc.uploadedAt)}
                    {doc.uploadedBy === 'staff' ? ' · by advisor' : ''}
                  </ItemDescription>
                </ItemContent>
                <ItemActions className="gap-1.5">
                  <Badge variant={STATUS_VARIANT[doc.status]}>
                    {DOCUMENT_STATUS_LABEL[doc.status]}
                  </Badge>
                  {onGetPassword && doc.kind === 'cas' && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      onClick={() => void handleGetPassword(doc)}
                      aria-label={`Reveal password for ${doc.fileName}`}
                    >
                      <KeyRound className="size-3.5" />
                    </Button>
                  )}
                  {onDownload ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      onClick={() => void handleDownload(doc)}
                      aria-label={`Download ${doc.fileName}`}
                    >
                      <Download className="size-3.5" />
                    </Button>
                  ) : (
                    <span className="text-muted-foreground text-[11px]">On file</span>
                  )}
                  {onDelete && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-muted-foreground size-7"
                      onClick={() => setDeleteTarget(doc)}
                      aria-label={`Delete ${doc.fileName}`}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </ItemActions>
              </Item>
            )
          })}
        </ItemGroup>
      )}

      {onUpload && (
        <UploadDocumentDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onUpload={onUpload}
          uploadProgress={uploadProgress}
          isUploading={isUploading}
          onCancelUpload={() => onCancelUpload?.()}
        />
      )}

      {onDelete && (
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {deleteTarget?.fileName}?</AlertDialogTitle>
              <AlertDialogDescription>
                This can&apos;t be undone — the stored file (and its password, if any) is deleted
                permanently.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {onGetPassword && (
        <PasswordRevealDialog
          result={passwordResult}
          onOpenChange={(open) => !open && setPasswordResult(null)}
        />
      )}
    </div>
  )
}
